import { HttpStatusCode } from "axios";
import { useCallback, useEffect, useRef } from "react";
import { showToast } from "../components/custom/custom-toast";
import {
  BasePaginateResponse,
  BaseResponse,
} from "../models/response/base-response";

type ResponseHandler<T> = (data: T) => void;
type ErrorHandler = (message: string) => void;

const useResponseHandler = <T = any,>(
  response: BaseResponse<T> | null | undefined,
  onSuccess?: ResponseHandler<T>,
  onError?: ErrorHandler
) => {
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!response) {
      return;
    }
    if (response.status === HttpStatusCode.Ok) {
      onSuccessRef.current?.(response.data);
    } else {
      onErrorRef.current?.(response.message);
    }
  }, [response]);
};

const useCRUDResponse = <T = any,>(
  response: BaseResponse<T> | null | undefined,
  onSuccess?: ResponseHandler<T>,
  successMessage?: string,
  errorHandler?: () => void
): void => {
  const handleSuccess = useCallback(
    (data: T) => {
      onSuccess?.(data);
      if (successMessage) {
        showToast({ message: successMessage, type: "success" });
      }
    },
    [onSuccess, successMessage]
  );

  const handleError = useCallback(
    (message: string) => {
      showToast({
        message: message ?? "Đã xảy ra lỗi chưa xác định",
        type: "warning",
      });
      errorHandler?.();
    },
    [errorHandler]
  );

  useResponseHandler<T>(response, handleSuccess, handleError);
};

const useDataResponse = <T = any,>(
  response: BaseResponse<T> | null | undefined,
  setTableData: (data: T) => void
): void => {
  const handleSuccess = useCallback(
    (data: T) => {
      setTableData(data);
    },
    [setTableData]
  );

  const handleError = useCallback((message: string) => {
    showToast({
      message: message ?? "Đã xảy ra lỗi chưa xác định",
      type: "warning",
    });
  }, []);

  useResponseHandler<T>(response, handleSuccess, handleError);
};

const useTableResponse = <T = any,>(
  response: BaseResponse<T[]> | null | undefined,
  page: number,
  limit: number,
  setTableData: (data: T[]) => void,
  setFilterData: (data: T[]) => void
): void => {
  const handleSuccess = useCallback(
    (data: T[]) => {
      setTableData(data.slice((page - 1) * limit, page * limit));
      setFilterData(data);
    },
    [page, limit, setTableData, setFilterData]
  );

  const handleError = useCallback((message: string) => {
    showToast({
      message: message ?? "Đã xảy ra lỗi chưa xác định",
      type: "warning",
    });
  }, []);

  useResponseHandler<T[]>(response, handleSuccess, handleError);
};

const useTablePaginationResponse = <T = any,>(
  response: BaseResponse<BasePaginateResponse<T[]>> | null | undefined,
  setTableData: (data: T[]) => void,
  setTotalRecord?: (totalRecord: number) => void
): void => {
  const handleSuccess = useCallback(
    (data: { limit: number; total_record: number; list: T[] }) => {
      setTableData(data.list);
      setTotalRecord?.(data.total_record);
    },
    [setTableData, setTotalRecord]
  );

  const handleError = useCallback((message: string) => {
    showToast({
      message: message ?? "Đã xảy ra lỗi chưa xác định",
      type: "warning",
    });
  }, []);
  useResponseHandler<{
    limit: number;
    total_record: number;
    list: T[];
  }>(response, handleSuccess, handleError);
};

export {
  useCRUDResponse,
  useDataResponse,
  useResponseHandler,
  useTablePaginationResponse,
  useTableResponse,
};
